import { useTranslation } from "react-i18next";
import format from "date-fns/format";
import cn from "classnames";

import StarsRateComponent from "components/common/StarsRateComponent";
import { MAX_RATE_COUNT } from "constants/settings";
import { FORMAT_DATE } from "constants/time";

import styles from "./styles.module.scss";

const PAYMENT_MARKS = {
    NOT_PAID: 1,
    DELAYED: 2,
    ON_TIME: 3,
} as const;

export interface Review {
    mark: number;
    paymentMark?: number;
    comment?: string;
    managerName: string;
    companyName?: string;
    date?: string;
    route: {
        from: string;
        to: string;
    };
}

interface CompanyReviewProps {
    review: Review;
}

export default function CompanyReview({ review }: CompanyReviewProps) {
    const { t } = useTranslation();

    return (
        <div className={styles.card}>
            {(review.companyName || review.date) && (
                <div className={styles.card__header}>
                    <span className={styles.card__companyName}>
                        {review.companyName}
                    </span>
                    {review.date && (
                        <span className={styles.card__date}>
                            {format(new Date(review.date), FORMAT_DATE)}
                        </span>
                    )}
                </div>
            )}

            <div className={styles.card__content}>
                <div className={styles.card__top}>
                    <StarsRateComponent
                        value={review.mark}
                        maxRate={MAX_RATE_COUNT}
                        onStarsCountChange={undefined}
                        className={undefined}
                    />
                    {!!review.paymentMark && (
                        <div className={styles.card__payment}>
                            <span className={styles.card__paymentLabel}>
                                {t("dialog.review.paymentLabel")}
                            </span>
                            <span
                                className={cn(styles.card__paymentBadge, {
                                    [styles["card__paymentBadge--green"]]:
                                        review.paymentMark === PAYMENT_MARKS.ON_TIME,
                                    [styles["card__paymentBadge--yellow"]]:
                                        review.paymentMark === PAYMENT_MARKS.DELAYED,
                                    [styles["card__paymentBadge--red"]]:
                                        review.paymentMark === PAYMENT_MARKS.NOT_PAID,
                                })}
                            >
                                {review.paymentMark === PAYMENT_MARKS.ON_TIME &&
                                    t("dialog.review.paymentOnTime")}
                                {review.paymentMark === PAYMENT_MARKS.DELAYED &&
                                    t("dialog.review.paymentDelayed")}
                                {review.paymentMark === PAYMENT_MARKS.NOT_PAID &&
                                    t("dialog.review.paymentNotPaid")}
                            </span>
                        </div>
                    )}
                </div>

                <hr className={styles.card__divider} />

                <div className={styles.card__details}>
                    <div className={styles.card__detail}>
                        <img
                            src={
                                new URL(
                                    "../../../../img/reviews/route.svg",
                                    import.meta.url,
                                ).href
                            }
                            alt=""
                            className={styles.card__icon}
                        />
                        <div className={styles.card__detailText}>
                            <span className={styles.card__detailLabel}>
                                {t("reviews.route")}
                            </span>
                            <span className={styles.card__detailValue}>
                                {review.route.from} — {review.route.to}
                            </span>
                        </div>
                    </div>
                    <div className={styles.card__detail}>
                        <img
                            src={
                                new URL("../../../../img/reviews/reviewer.svg", import.meta.url).href
                            }
                            alt=""
                            className={styles.card__icon}
                        />
                        <div className={styles.card__detailText}>
                            <span className={styles.card__detailLabel}>
                                {t("reviews.manager")}
                            </span>
                            <span className={styles.card__detailValue}>
                                {review.managerName}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {review.comment && (
                <div className={styles.card__commentBox}>
                    <span className={styles.card__commentLabel}>{t("comment")}</span>
                    <span className={styles.card__commentText}>{review.comment}</span>
                </div>
            )}
        </div>
    );
}
